// src/types/index.ts
var AddressType = {
  BILLING: "billing",
  SHIPPING: "shipping"
};
var InvoiceStatus = {
  DRAFT: "draft",
  SENT: "sent",
  PAID: "paid",
  OVERDUE: "overdue",
  CANCELLED: "cancelled"
};
var UnitType = {
  HOURS: "hours",
  DAYS: "days",
  PIECES: "pieces",
  FLAT_RATE: "flat_rate",
  KM: "km"
};
var DiscountType = {
  PERCENT: "percent",
  ABSOLUTE: "absolute"
};
var BillingInterval = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  SEMI_ANNUAL: "semi_annual",
  ANNUAL: "annual"
};
var EstimateStatus = {
  DRAFT: "draft",
  SENT: "sent",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  EXPIRED: "expired",
  CONVERTED: "converted"
};

// src/validation/common.ts
import { z } from "zod";
var uuidSchema = z.string().uuid();
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)");
var isoDateTimeSchema = z.string().datetime();
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(200).default(40)
});
var searchQuerySchema = z.object({
  q: z.string().optional()
});
var addressTypeSchema = z.enum(["billing", "shipping"]);
var invoiceStatusSchema = z.enum(["draft", "sent", "paid", "overdue", "cancelled", "deferred"]);
var unitTypeSchema = z.enum(["hours", "days", "pieces", "flat_rate", "km"]);
var discountTypeSchema = z.enum(["percent", "absolute"]);
var billingIntervalSchema = z.enum(["monthly", "quarterly", "semi_annual", "annual"]);
var estimateStatusSchema = z.enum([
  "draft",
  "sent",
  "accepted",
  "rejected",
  "expired",
  "converted"
]);
var loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required")
});
var versionSchema = z.object({
  version: z.number().int().positive()
});
var vatRateSchema = z.number().refine(
  (val) => [0, 7, 19].includes(val),
  { message: "VAT rate must be 0%, 7%, or 19%" }
);
var moneySchema = z.number().multipleOf(0.01);
var positiveMoneySchema = moneySchema.positive();
var nonNegativeMoneySchema = moneySchema.nonnegative();

// src/validation/customer.ts
import { z as z2 } from "zod";
var createCustomerSchema = z2.object({
  companyName: z2.string().min(1, "Firmenname ist erforderlich").max(255),
  contactFirstName: z2.string().max(100).optional(),
  contactLastName: z2.string().max(100).optional(),
  email: z2.string().email("Ung\xFCltige E-Mail-Adresse").max(255).optional().or(z2.literal("")),
  phone: z2.string().max(50).optional(),
  defaultPaymentDays: z2.number().int().min(0).max(365).optional(),
  notes: z2.string().optional()
});
var updateCustomerSchema = z2.object({
  companyName: z2.string().min(1, "Firmenname ist erforderlich").max(255).optional(),
  contactFirstName: z2.string().max(100).nullable().optional(),
  contactLastName: z2.string().max(100).nullable().optional(),
  email: z2.string().email("Ung\xFCltige E-Mail-Adresse").max(255).nullable().optional().or(z2.literal("")),
  phone: z2.string().max(50).nullable().optional(),
  defaultPaymentDays: z2.number().int().min(0).max(365).nullable().optional(),
  notes: z2.string().nullable().optional(),
  version: z2.number().int().positive("Version ist erforderlich")
});
var archiveCustomerSchema = z2.object({
  isArchived: z2.boolean(),
  version: z2.number().int().positive()
});
var customerListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  isArchived: z2.preprocess(
    (val) => val === "true" || val === true,
    z2.boolean()
  ).optional(),
  sortBy: z2.enum(["companyName", "customerNumber", "createdAt", "updatedAt"]).optional().default("companyName"),
  sortOrder: z2.enum(["asc", "desc"]).optional().default("asc")
});
var customerIdParamSchema = z2.object({
  id: uuidSchema
});

// src/validation/address.ts
import { z as z3 } from "zod";
var createAddressSchema = z3.object({
  addressType: addressTypeSchema,
  isDefault: z3.boolean().optional().default(false),
  street: z3.string().min(1, "Stra\xDFe ist erforderlich").max(255),
  street2: z3.string().max(255).optional(),
  postalCode: z3.string().min(1, "PLZ ist erforderlich").max(20),
  city: z3.string().min(1, "Stadt ist erforderlich").max(100),
  country: z3.string().length(2, "L\xE4ndercode muss 2 Zeichen haben").optional().default("DE")
});
var updateAddressSchema = z3.object({
  addressType: addressTypeSchema.optional(),
  isDefault: z3.boolean().optional(),
  street: z3.string().min(1, "Stra\xDFe ist erforderlich").max(255).optional(),
  street2: z3.string().max(255).nullable().optional(),
  postalCode: z3.string().min(1, "PLZ ist erforderlich").max(20).optional(),
  city: z3.string().min(1, "Stadt ist erforderlich").max(100).optional(),
  country: z3.string().length(2, "L\xE4ndercode muss 2 Zeichen haben").optional()
});
var addressIdParamSchema = z3.object({
  id: uuidSchema
});
var customerAddressParamSchema = z3.object({
  customerId: uuidSchema
});
var addressWithCustomerParamSchema = z3.object({
  customerId: uuidSchema,
  addressId: uuidSchema
});

// src/validation/invoice.ts
import { z as z4 } from "zod";
var billingAddressSnapshotSchema = z4.object({
  companyName: z4.string(),
  contactFirstName: z4.string().optional(),
  contactLastName: z4.string().optional(),
  street: z4.string(),
  street2: z4.string().optional(),
  postalCode: z4.string(),
  city: z4.string(),
  country: z4.string().length(2).default("DE")
});
var createInvoiceSchema = z4.object({
  customerId: uuidSchema,
  billingAddressId: uuidSchema.optional(),
  invoiceDate: isoDateSchema.optional(),
  // Defaults to today
  dueDate: isoDateSchema.optional(),
  // Can be auto-calculated from customer payment days
  vatRate: vatRateSchema.optional().default(19),
  overallDiscountPercent: z4.number().min(0).max(100).optional(),
  notes: z4.string().optional(),
  internalNotes: z4.string().optional(),
  showInternalNotesOnPdf: z4.boolean().optional(),
  customerReference: z4.string().max(100).optional()
});
var updateInvoiceSchema = z4.object({
  billingAddressId: uuidSchema.nullable().optional(),
  invoiceDate: isoDateSchema.optional(),
  dueDate: isoDateSchema.optional(),
  vatRate: vatRateSchema.optional(),
  overallDiscountPercent: z4.number().min(0).max(100).nullable().optional(),
  notes: z4.string().nullable().optional(),
  internalNotes: z4.string().nullable().optional(),
  showInternalNotesOnPdf: z4.boolean().optional(),
  customerReference: z4.string().max(100).nullable().optional(),
  version: z4.number().int().positive("Version ist erforderlich")
});
var markInvoicePaidSchema = z4.object({
  paymentDate: isoDateSchema.optional(),
  // Defaults to today
  version: z4.number().int().positive()
});
var updateInvoicePaymentDateSchema = z4.object({
  paymentDate: isoDateSchema,
  version: z4.number().int().positive()
});
var markInvoiceSentSchema = z4.object({
  version: z4.number().int().positive()
});
var cancelInvoiceSchema = z4.object({
  notes: z4.string().optional(),
  version: z4.number().int().positive()
});
var markInvoiceDeferredSchema = z4.object({
  version: z4.number().int().positive()
});
var unDeferInvoiceSchema = z4.object({
  version: z4.number().int().positive()
});
var updateInvoiceNotesSchema = z4.object({
  internalNotes: z4.string().nullable().optional(),
  showInternalNotesOnPdf: z4.boolean().optional()
});
var invoiceListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  customerId: uuidSchema.optional(),
  status: z4.union([invoiceStatusSchema, z4.array(invoiceStatusSchema)]).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  overdue: z4.preprocess(
    (val) => val === "true" || val === true,
    z4.boolean()
  ).optional(),
  sortBy: z4.enum(["invoiceNumber", "invoiceDate", "dueDate", "grossTotal", "status", "createdAt", "paymentDate"]).optional().default("invoiceDate"),
  sortOrder: z4.enum(["asc", "desc"]).optional().default("desc")
});
var invoiceIdParamSchema = z4.object({
  id: uuidSchema
});

// src/validation/invoice-item.ts
import { z as z5 } from "zod";
var createInvoiceItemSchema = z5.object({
  description: z5.string().min(1, "Beschreibung ist erforderlich"),
  quantity: z5.number().positive("Menge muss positiv sein").default(1),
  unitType: unitTypeSchema.default("pieces"),
  unitPrice: moneySchema,
  // Allows negative for discount/rebate lines
  discountType: discountTypeSchema.optional(),
  discountValue: nonNegativeMoneySchema.optional()
}).refine(
  (data) => {
    if (data.discountType && data.discountValue === void 0) {
      return false;
    }
    if (data.discountType === "percent" && data.discountValue !== void 0) {
      return data.discountValue >= 0 && data.discountValue <= 100;
    }
    return true;
  },
  {
    message: "Ung\xFCltiger Rabatt"
  }
);
var updateInvoiceItemSchema = z5.object({
  description: z5.string().min(1, "Beschreibung ist erforderlich").optional(),
  quantity: z5.number().positive("Menge muss positiv sein").optional(),
  unitType: unitTypeSchema.optional(),
  unitPrice: moneySchema.optional(),
  discountType: discountTypeSchema.nullable().optional(),
  discountValue: nonNegativeMoneySchema.nullable().optional()
}).refine(
  (data) => {
    if (data.discountType === "percent" && data.discountValue !== void 0 && data.discountValue !== null) {
      return data.discountValue >= 0 && data.discountValue <= 100;
    }
    return true;
  },
  {
    message: "Prozentrabatt muss zwischen 0 und 100 liegen"
  }
);
var reorderInvoiceItemsSchema = z5.object({
  itemIds: z5.array(uuidSchema).min(1, "Mindestens eine Position erforderlich")
});
var invoiceItemIdParamSchema = z5.object({
  invoiceId: uuidSchema,
  itemId: uuidSchema
});

// src/validation/reminder.ts
import { z as z6 } from "zod";
var reminderLevelConfigSchema = z6.object({
  level: z6.number().int().min(1).max(3),
  defaultFee: z6.number().min(0).default(0),
  defaultDueDays: z6.number().int().min(1).default(14),
  defaultInterestRate: z6.number().min(0).max(100).optional()
});
var createReminderSchema = z6.object({
  invoiceId: uuidSchema,
  reminderDate: isoDateSchema.optional(),
  // Defaults to today
  dueDate: isoDateSchema.optional(),
  // Auto-calculated if not provided
  reminderFee: z6.number().min(0).optional().default(0),
  interestRate: z6.number().min(0).max(100).optional(),
  notes: z6.string().optional()
});
var updateReminderSchema = z6.object({
  dueDate: isoDateSchema.optional(),
  reminderFee: z6.number().min(0).optional(),
  interestRate: z6.number().min(0).max(100).optional(),
  notes: z6.string().nullable().optional()
});
var reminderListQuerySchema = paginationQuerySchema.extend({
  invoiceId: uuidSchema.optional(),
  level: z6.coerce.number().int().min(1).optional()
});
var reminderIdParamSchema = z6.object({
  id: uuidSchema
});
var invoiceReminderParamSchema = z6.object({
  invoiceId: uuidSchema
});

// src/validation/contract.ts
import { z as z7 } from "zod";
var contractStatusSchema = z7.enum([
  "draft",
  "active",
  "paused",
  "completed",
  "cancelled"
]);
var contractBillingIntervalSchema = z7.enum([
  "monthly",
  "quarterly",
  "semiannually",
  "annually"
]);
var contractItemInputSchema = z7.object({
  id: z7.string().uuid().optional(),
  position: z7.number().int().positive(),
  description: z7.string().min(1).max(500),
  quantity: z7.number().positive().default(1),
  unit: z7.string().max(20).default("St\xFCck"),
  unitPrice: z7.number().nonnegative(),
  longDescription: z7.string().optional().nullable()
});
var createContractSchema = z7.object({
  title: z7.string().min(1, "Titel ist erforderlich").max(255),
  description: z7.string().optional().nullable(),
  customerId: z7.string().uuid("Ung\xFCltige Kunden-ID"),
  startDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Ung\xFCltiges Datum"),
  endDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  billingInterval: contractBillingIntervalSchema.default("monthly"),
  billingDayOfMonth: z7.number().int().min(1).max(28).default(1),
  vatRate: z7.number().min(0).max(100).default(19),
  customerReference: z7.string().max(100).optional().nullable(),
  invoicePrefix: z7.string().max(20).optional().nullable(),
  autoGenerate: z7.boolean().default(true),
  items: z7.array(contractItemInputSchema).min(1, "Mindestens eine Position erforderlich")
});
var updateContractSchema = z7.object({
  title: z7.string().min(1).max(255).optional(),
  description: z7.string().optional().nullable(),
  status: contractStatusSchema.optional(),
  startDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  terminationDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  billingInterval: contractBillingIntervalSchema.optional(),
  billingDayOfMonth: z7.number().int().min(1).max(28).optional(),
  vatRate: z7.number().min(0).max(100).optional(),
  customerReference: z7.string().max(100).optional().nullable(),
  invoicePrefix: z7.string().max(20).optional().nullable(),
  autoGenerate: z7.boolean().optional(),
  items: z7.array(contractItemInputSchema).optional()
});
var contractWithRelationsSchema = z7.object({
  id: z7.string().uuid(),
  contractNumber: z7.string(),
  title: z7.string(),
  description: z7.string().nullable(),
  customerId: z7.string().uuid(),
  status: contractStatusSchema,
  startDate: z7.string(),
  endDate: z7.string().nullable(),
  terminationDate: z7.string().nullable(),
  billingInterval: contractBillingIntervalSchema,
  billingDayOfMonth: z7.number(),
  nextBillingDate: z7.string().nullable(),
  lastBillingDate: z7.string().nullable(),
  vatRate: z7.string(),
  customerReference: z7.string().nullable(),
  invoicePrefix: z7.string().nullable(),
  autoGenerate: z7.boolean(),
  createdAt: z7.string(),
  updatedAt: z7.string(),
  version: z7.number(),
  customer: z7.object({
    id: z7.string().uuid(),
    customerNumber: z7.string(),
    companyName: z7.string()
  }).optional(),
  items: z7.array(contractItemInputSchema)
});
var listContractsQuerySchema = z7.object({
  page: z7.coerce.number().int().positive().default(1),
  limit: z7.coerce.number().int().positive().max(100).default(20),
  status: contractStatusSchema.optional(),
  customerId: z7.string().uuid().optional(),
  search: z7.string().optional(),
  sortBy: z7.enum(["contractNumber", "title", "startDate", "nextBillingDate", "createdAt"]).default("createdAt"),
  sortDirection: z7.enum(["asc", "desc"]).default("desc")
});
var contractBillingIntervalLabels = {
  monthly: "Monatlich",
  quarterly: "Viertelj\xE4hrlich",
  semiannually: "Halbj\xE4hrlich",
  annually: "J\xE4hrlich"
};
var contractBillingIntervalMonths = {
  monthly: 1,
  quarterly: 3,
  semiannually: 6,
  annually: 12
};
var contractStatusLabels = {
  draft: "Entwurf",
  active: "Aktiv",
  paused: "Pausiert",
  completed: "Abgeschlossen",
  cancelled: "Storniert"
};
var contractStatusColors = {
  draft: "bg-white/10 text-white/60",
  active: "bg-green-500/20 text-green-400",
  paused: "bg-yellow-500/20 text-yellow-400",
  completed: "bg-blue-500/20 text-blue-400",
  cancelled: "bg-red-500/20 text-red-400"
};

// src/validation/estimate.ts
import { z as z8 } from "zod";
var estimateStatusLabels = {
  draft: "Entwurf",
  sent: "Versendet",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
  converted: "Umgewandelt"
};
var estimateStatusColors = {
  draft: "bg-gray-500/20 text-gray-400",
  sent: "bg-blue-500/20 text-blue-400",
  accepted: "bg-green-500/20 text-green-400",
  rejected: "bg-red-500/20 text-red-400",
  expired: "bg-orange-500/20 text-orange-400",
  converted: "bg-purple-500/20 text-purple-400"
};
var estimateItemInputSchema = z8.object({
  position: z8.number().int().min(1),
  description: z8.string().min(1).max(500),
  longDescription: z8.string().max(2e3).optional(),
  quantity: z8.number().positive(),
  unit: z8.string().max(20).default("St\xFCck"),
  unitPrice: z8.number().nonnegative()
});
var createEstimateSchema = z8.object({
  customerId: z8.string().uuid(),
  issueDate: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validUntil: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  subject: z8.string().max(255).optional(),
  customerReference: z8.string().max(100).optional(),
  introText: z8.string().max(2e3).optional(),
  closingText: z8.string().max(2e3).optional(),
  vatRate: z8.number().min(0).max(100).default(19),
  items: z8.array(estimateItemInputSchema).min(1)
});
var updateEstimateSchema = z8.object({
  issueDate: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  validUntil: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  subject: z8.string().max(255).nullable().optional(),
  customerReference: z8.string().max(100).nullable().optional(),
  introText: z8.string().max(2e3).nullable().optional(),
  closingText: z8.string().max(2e3).nullable().optional(),
  vatRate: z8.number().min(0).max(100).optional(),
  items: z8.array(estimateItemInputSchema).min(1).optional()
});
var listEstimatesQuerySchema = z8.object({
  page: z8.coerce.number().int().min(1).default(1),
  limit: z8.coerce.number().int().min(1).max(100).default(20),
  status: estimateStatusSchema.optional(),
  customerId: z8.string().uuid().optional(),
  search: z8.string().optional(),
  sortBy: z8.enum(["issueDate", "estimateNumber", "grossTotal", "validUntil"]).default("issueDate"),
  sortOrder: z8.enum(["asc", "desc"]).default("desc")
});

// src/validation/audit.ts
import { z as z9 } from "zod";
var auditEntityIdSchema = uuidSchema;
var auditEntityTypeSchema = z9.enum([
  "user",
  "customer",
  "address",
  "invoice",
  "invoice_item",
  "payment_reminder",
  "contract",
  "contract_item",
  "cost_estimate",
  "estimate_item",
  "settings"
]);
var auditActionSchema = z9.enum(["create", "update", "delete"]);
var auditListQuerySchema = paginationQuerySchema.extend({
  entityType: auditEntityTypeSchema.optional(),
  entityId: auditEntityIdSchema.optional(),
  action: auditActionSchema.optional(),
  userId: z9.string().optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional()
});
var auditEntityParamSchema = z9.object({
  entityType: auditEntityTypeSchema,
  entityId: auditEntityIdSchema
});
var auditIdParamSchema = z9.object({
  id: z9.string().uuid()
});

// src/utils/calculations.ts
function calculateLineTotal(quantity, unitPrice, discountType, discountValue) {
  const base = quantity * unitPrice;
  if (!discountType || discountValue === void 0 || discountValue === 0) {
    return round(base);
  }
  if (discountType === "percent") {
    return round(base * (1 - discountValue / 100));
  }
  return round(base - discountValue);
}
function calculateInvoiceTotals(items, vatRate, overallDiscountPercent = 0) {
  const subtotal = round(items.reduce((sum, item) => sum + item.lineTotal, 0));
  const overallDiscountAmount = round(subtotal * overallDiscountPercent / 100);
  const netTotal = round(subtotal - overallDiscountAmount);
  const vatAmount = round(netTotal * (vatRate / 100));
  const grossTotal = round(netTotal + vatAmount);
  return {
    subtotal,
    overallDiscountAmount,
    netTotal,
    vatAmount,
    grossTotal
  };
}
function calculateDiscountBreakdown(items, netTotal) {
  const subtotal = round(items.reduce(
    (sum, item) => sum + Number(item.lineTotal),
    0
  ));
  return { subtotal, discount: round(subtotal - Number(netTotal)) };
}
function calculateInterest(principalAmount, annualRate, daysOverdue) {
  const dailyRate = annualRate / 100 / 365;
  const interest = principalAmount * dailyRate * daysOverdue;
  return round(interest);
}
function calculateReminderTotal(originalAmount, reminderFee, interestAmount) {
  return round(originalAmount + reminderFee + interestAmount);
}
function round(value) {
  return Math.round(value * 100) / 100;
}
function daysBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1e3 * 60 * 60 * 24));
}
function isOverdue(dueDate) {
  const due = new Date(dueDate);
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

// src/utils/format.ts
function formatCurrency(amount) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
}
function formatNumber(value, decimals = 2) {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}
function formatPaymentTerm(days) {
  if (days === 0) return "Sofort";
  return `${days} ${days === 1 ? "Tag" : "Tage"}`;
}
function formatDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE").format(d);
}
function formatDateTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(d);
}
function formatRelativeTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  const rtf = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1e3 * 60));
      return rtf.format(-diffMinutes, "minute");
    }
    return rtf.format(-diffHours, "hour");
  }
  if (diffDays < 30) {
    return rtf.format(-diffDays, "day");
  }
  if (diffDays < 365) {
    const diffMonths = Math.floor(diffDays / 30);
    return rtf.format(-diffMonths, "month");
  }
  const diffYears = Math.floor(diffDays / 365);
  return rtf.format(-diffYears, "year");
}
function getUnitTypeLabel(unitType, quantity) {
  const labels = {
    hours: "Std.",
    days: ["Tag", "Tage"],
    pieces: "Stk.",
    flat_rate: "pauschal",
    km: "km"
  };
  const label = labels[unitType];
  if (Array.isArray(label)) {
    return quantity === 1 ? label[0] : label[1];
  }
  return label;
}
function getUnitTypeFullName(unitType) {
  const names = {
    hours: "Stunden",
    days: "Tage",
    pieces: "St\xFCck",
    flat_rate: "Pauschal",
    km: "Kilometer"
  };
  return names[unitType];
}
function formatInvoiceNumber(year, sequence) {
  return `${year}-${String(sequence).padStart(4, "0")}`;
}
function formatCustomerNumber(sequence) {
  return `K-${String(sequence).padStart(5, "0")}`;
}
function formatContractNumber(year, sequence) {
  return `V-${year}-${String(sequence).padStart(4, "0")}`;
}
function formatEstimateNumber(year, sequence) {
  return `KV-${year}-${String(sequence).padStart(4, "0")}`;
}
function formatIBAN(iban) {
  return iban.replace(/(.{4})/g, "$1 ").trim();
}
function getQuarter(date) {
  let year;
  let month;
  if (typeof date === "string") {
    const calendarDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (calendarDate) {
      year = Number(calendarDate[1]);
      month = Number(calendarDate[2]);
    } else {
      const d = new Date(date);
      year = d.getFullYear();
      month = d.getMonth() + 1;
    }
  } else {
    year = date.getFullYear();
    month = date.getMonth() + 1;
  }
  const quarter = Math.ceil(month / 3);
  return { quarter, year };
}
function formatQuarter(quarter, year) {
  return `Q${quarter} ${year}`;
}
function formatPhoneNumber(phone) {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+49")) {
    const rest = cleaned.slice(3);
    if (rest.length >= 10) {
      return `+49 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
    }
  }
  return cleaned;
}
export {
  AddressType,
  BillingInterval,
  DiscountType,
  EstimateStatus,
  InvoiceStatus,
  UnitType,
  addressIdParamSchema,
  addressTypeSchema,
  addressWithCustomerParamSchema,
  archiveCustomerSchema,
  auditActionSchema,
  auditEntityParamSchema,
  auditEntityTypeSchema,
  auditIdParamSchema,
  auditListQuerySchema,
  billingAddressSnapshotSchema,
  billingIntervalSchema,
  calculateDiscountBreakdown,
  calculateInterest,
  calculateInvoiceTotals,
  calculateLineTotal,
  calculateReminderTotal,
  cancelInvoiceSchema,
  contractBillingIntervalLabels,
  contractBillingIntervalMonths,
  contractBillingIntervalSchema,
  contractItemInputSchema,
  contractStatusColors,
  contractStatusLabels,
  contractStatusSchema,
  contractWithRelationsSchema,
  createAddressSchema,
  createContractSchema,
  createCustomerSchema,
  createEstimateSchema,
  createInvoiceItemSchema,
  createInvoiceSchema,
  createReminderSchema,
  customerAddressParamSchema,
  customerIdParamSchema,
  customerListQuerySchema,
  daysBetween,
  discountTypeSchema,
  estimateItemInputSchema,
  estimateStatusColors,
  estimateStatusLabels,
  estimateStatusSchema,
  formatContractNumber,
  formatCurrency,
  formatCustomerNumber,
  formatDate,
  formatDateTime,
  formatEstimateNumber,
  formatIBAN,
  formatInvoiceNumber,
  formatNumber,
  formatPaymentTerm,
  formatPhoneNumber,
  formatQuarter,
  formatRelativeTime,
  getQuarter,
  getUnitTypeFullName,
  getUnitTypeLabel,
  invoiceIdParamSchema,
  invoiceItemIdParamSchema,
  invoiceListQuerySchema,
  invoiceReminderParamSchema,
  invoiceStatusSchema,
  isOverdue,
  isoDateSchema,
  isoDateTimeSchema,
  listContractsQuerySchema,
  listEstimatesQuerySchema,
  loginSchema,
  markInvoiceDeferredSchema,
  markInvoicePaidSchema,
  markInvoiceSentSchema,
  moneySchema,
  nonNegativeMoneySchema,
  paginationQuerySchema,
  positiveMoneySchema,
  reminderIdParamSchema,
  reminderLevelConfigSchema,
  reminderListQuerySchema,
  reorderInvoiceItemsSchema,
  round,
  searchQuerySchema,
  unDeferInvoiceSchema,
  unitTypeSchema,
  updateAddressSchema,
  updateContractSchema,
  updateCustomerSchema,
  updateEstimateSchema,
  updateInvoiceItemSchema,
  updateInvoiceNotesSchema,
  updateInvoicePaymentDateSchema,
  updateInvoiceSchema,
  updateReminderSchema,
  uuidSchema,
  vatRateSchema,
  versionSchema
};
//# sourceMappingURL=index.js.map